import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { TextValidator } from "react-material-ui-form-validator";
import {
  ApiAlertIntervalType,
  ApiAlertRequirementPeriod,
  ApiAlertRequirementResponse,
  ApiAlertResponse,
  ApiMetric,
} from "@incendium/api";
import produce from "immer";
import { Add } from "@mui/icons-material";
import {
  AccordianChartBuilderSidebarBlock,
  ChartBuilderSidebarBlock,
} from "features/chartLibrary";
import { MetricSelector, metricsFromChart } from "features/analytics";
import { useCallback, useEffect, useMemo, useState } from "react";
import AnimatedList from "Components/AnimatedList/AnimatedList";
import { parseChartFromResponse } from "features/chartLibrary/utils";

const intervalTypeOptions: {
  value: ApiAlertIntervalType;
  label: string;
  unit?: string;
}[] = [
  { value: ApiAlertIntervalType.ALERT_PERIOD_TYPE_ASAP, label: "ASAP" },
  {
    value: ApiAlertIntervalType.ALERT_PERIOD_TYPE_HOURLY,
    label: "Hourly",
    unit: "hours",
  },
  {
    value: ApiAlertIntervalType.ALERT_PERIOD_TYPE_DAILY,
    label: "Daily",
    unit: "days",
  },
  {
    value: ApiAlertIntervalType.ALERT_PERIOD_TYPE_WEEKLY,
    label: "Weekly",
    unit: "weeks",
  },
  {
    value: ApiAlertIntervalType.ALERT_PERIOD_TYPE_MONTHLY,
    label: "Monthly",
    unit: "months",
  },
];

const requirementPeriodOptions: {
  value: ApiAlertRequirementPeriod;
  label: string;
  helper: string;
}[] = [
  {
    value: ApiAlertRequirementPeriod.PREVIOUS,
    label: "Previous period",
    helper: "The same number of days immediately before",
  },
  {
    value: ApiAlertRequirementPeriod.PREVIOUS_MONTH,
    label: "Same days last month",
    helper: "The same number of days, shifted back one month",
  },
  {
    value: ApiAlertRequirementPeriod.PREVIOUS_YEAR,
    label: "Same days last year",
    helper: "The same number of days, shifted back one year",
  },
];

function MetricSelectorRow({
  row,
  onEdit,
}: {
  row: ApiMetric;
  onEdit: (v: ApiMetric) => void;
}) {
  return (
    <MetricSelector
      metric={row}
      overrideIsStatic
      hasControls={false}
      onChange={(e, v) => onEdit(v?.metric || ApiMetric.METRIC_NOT_SET)}
    />
  );
}

interface IAlertSidebarProps {
  alert: ApiAlertResponse;
  setAlert: React.Dispatch<React.SetStateAction<ApiAlertResponse | undefined>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function AlertSidebar({ alert, setAlert, setOpen }: IAlertSidebarProps) {
  const recipients = alert.emails || [];
  const requirement = alert.requirements?.[0];
  const [metrics, setMetrics] = useState<ApiMetric[]>(
    metricsFromChart(
      parseChartFromResponse(alert.chartNotifications?.[0]?.chart || {}),
    ),
  );

  const selectedIntervalType = useMemo(
    () =>
      intervalTypeOptions.find((o) => o.value === alert.intervalType) ||
      intervalTypeOptions[0],
    [alert.intervalType],
  );
  const showIntervalCount =
    selectedIntervalType.value !== ApiAlertIntervalType.ALERT_PERIOD_TYPE_ASAP;

  const selectedPeriod =
    requirementPeriodOptions.find((o) => o.value === requirement?.period) ||
    requirementPeriodOptions[0];

  const updateRequirement = useCallback(
    (updater: (requirement: ApiAlertRequirementResponse) => void) => {
      setAlert(
        produce(alert, (draft) => {
          if (!draft.requirements?.length) {
            draft.requirements = [{}];
          }
          updater(draft.requirements![0]);
        }),
      );
    },
    [alert, setAlert],
  );

  useEffect(() => {
    setAlert((alert) =>
      produce(alert, (draft) => {
        if (!draft) return;
        if (!draft.chartNotifications) {
          draft.chartNotifications = [];
        }
        draft.chartNotifications[0] = {
          ...draft.chartNotifications[0],
          chart: {
            ...(draft.chartNotifications?.[0]?.chart || {}),
            yAxisKeys: [
              {
                ...(draft.chartNotifications?.[0]?.chart?.yAxisKeys?.[0] || {}),
                key: "l",
                fields: metrics.map((m) => m),
              },
            ],
          },
        };
      }),
    );
  }, [metrics]);

  return (
    <Box>
      <ChartBuilderSidebarBlock>
        <Typography variant="h3">Alert Setup</Typography>
      </ChartBuilderSidebarBlock>

      <AccordianChartBuilderSidebarBlock
        title="Details"
        subTitle="Name and describe your alert."
      >
        <Stack spacing={2} mb={1}>
          <TextValidator
            label="name of alert"
            variant="outlined"
            name="name"
            size="small"
            fullWidth
            validators={["required"]}
            errorMessages={["name is required"]}
            value={alert.name || ""}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setAlert(
                produce(alert, (draft) => {
                  draft.name = e.currentTarget.value;
                }),
              );
            }}
          />
          <TextField
            label="description"
            variant="outlined"
            name="description"
            size="small"
            fullWidth
            value={alert.description || ""}
            multiline
            minRows={4}
            maxRows={8}
            onChange={(e) => {
              setAlert(
                produce(alert, (draft) => {
                  draft.description = e.currentTarget.value;
                }),
              );
            }}
          />
        </Stack>
      </AccordianChartBuilderSidebarBlock>

      <AccordianChartBuilderSidebarBlock
        title="Schedule"
        subTitle="How often should we check this alert?"
      >
        <Stack spacing={2} mb={1}>
          <TextField
            select
            fullWidth
            size="small"
            label="Frequency"
            value={
              alert.intervalType || ApiAlertIntervalType.ALERT_PERIOD_TYPE_ASAP
            }
            onChange={(e) => {
              const intervalType = e.target.value as ApiAlertIntervalType;
              setAlert(
                produce(alert, (draft) => {
                  draft.intervalType = intervalType;
                  if (
                    intervalType !==
                      ApiAlertIntervalType.ALERT_PERIOD_TYPE_ASAP &&
                    !draft.interval
                  ) {
                    draft.interval = 1;
                  }
                }),
              );
            }}
          >
            {intervalTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {showIntervalCount && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label={`Every (in ${selectedIntervalType.unit})`}
              value={alert.interval || 1}
              inputProps={{ min: 1 }}
              onChange={(e) => {
                const value = Math.max(1, Number(e.target.value) || 1);
                setAlert(
                  produce(alert, (draft) => {
                    draft.interval = value;
                  }),
                );
              }}
            />
          )}
        </Stack>
      </AccordianChartBuilderSidebarBlock>

      <AccordianChartBuilderSidebarBlock
        title="Data Window"
        subTitle="How much recent data to evaluate, and what increase/decrease compares against."
      >
        <Stack spacing={2} mb={1}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Last N days"
            helperText="How many days of data to look at when checking this alert."
            value={requirement?.chartLastNDays || 1}
            inputProps={{ min: 1 }}
            onChange={(e) => {
              const chartLastNDays = Math.max(1, Number(e.target.value) || 1);
              updateRequirement((req) => {
                req.chartLastNDays = chartLastNDays;
              });
            }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Compare change against"
            helperText={selectedPeriod.helper}
            value={
              requirement?.period || ApiAlertRequirementPeriod.PREVIOUS
            }
            onChange={(e) => {
              const period = e.target.value as ApiAlertRequirementPeriod;
              updateRequirement((req) => {
                req.period = period;
              });
            }}
          >
            {requirementPeriodOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </AccordianChartBuilderSidebarBlock>

      <AccordianChartBuilderSidebarBlock
        title="Extra Data"
        subTitle="Add additional metrics you would like to see when the alert triggers."
      >
        <AnimatedList
          allowDeleteAll
          btnText="Add Metric"
          onAdd={() => {
            setMetrics(
              produce(metrics, (draft) => {
                draft.push(ApiMetric.METRIC_NOT_SET);
              }),
            );
          }}
          onRemove={(idx) => {
            setMetrics(
              produce(metrics, (draft) => {
                draft.splice(idx, 1);
              }),
            );
          }}
          onEdit={(idx) => (metric) => {
            setMetrics(
              produce(metrics, (draft) => {
                draft[idx] = metric as ApiMetric;
              }),
            );
          }}
          items={metrics}
          RowComponent={MetricSelectorRow}
        />
      </AccordianChartBuilderSidebarBlock>
      <AccordianChartBuilderSidebarBlock
        title="Recipients"
        subTitle="Who should we contact when this alert triggers?"
      >
        {recipients.length > 0 && (
          <Box display="flex" flexWrap="wrap" mb={1}>
            {recipients.map((recipient, idx) => (
              <Box key={recipient} mr={1} mb={1}>
                <Chip
                  sx={{ maxWidth: 300 }}
                  label={
                    <Typography variant="body2" color="inherit" noWrap>
                      {recipient}
                    </Typography>
                  }
                  onClick={() => setOpen(true)}
                  onDelete={() => {
                    setAlert(
                      produce(alert, (draft) => {
                        draft.emails?.splice(idx, 1);
                      }),
                    );
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
        <Box mb={1}>
          <Button
            color="secondary"
            size="extraSmall"
            endIcon={<Add />}
            onClick={() => setOpen(true)}
          >
            {recipients.length > 0 ? "Manage Recipients" : "Add Recipients"}
          </Button>
        </Box>
      </AccordianChartBuilderSidebarBlock>
    </Box>
  );
}

export default AlertSidebar;
